import React, { useEffect, useState } from 'react';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

const DEFAULT_ADDRESS = {
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  country: 'United Kingdom',
};

const StepPersonalBasics = ({ formData, updateFormData, errors }) => {
  const [phoneInput, setPhoneInput] = useState(formData.phoneNumber || '');
  const [address, setAddress] = useState({
    ...DEFAULT_ADDRESS,
    ...(formData.homeAddress || {}),
  });

  useEffect(() => {
    setPhoneInput(formData.phoneNumber || '');
  }, [formData.phoneNumber]);

  useEffect(() => {
    setAddress({
      ...DEFAULT_ADDRESS,
      ...(formData.homeAddress || {}),
    });
  }, [formData.homeAddress]);

  const handleAddressChange = (field, value) => {
    const nextAddress = { ...address, [field]: value };
    setAddress(nextAddress);
    updateFormData({ homeAddress: nextAddress });
  };

  const handlePhoneChange = (value) => {
    setPhoneInput(value);

    let formattedNumber = value;
    let phoneVerified = false;

    if (isValidPhoneNumber(value)) {
      try {
        const parsed = parsePhoneNumber(value);
        formattedNumber = parsed.number;
        phoneVerified = true;
      } catch (err) {
        formattedNumber = value;
        phoneVerified = false;
      }
    }

    updateFormData({
      phoneNumber: formattedNumber,
      phoneVerified,
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <label className="block text-body-md font-medium text-text-primary mb-2">
          Phone number *
        </label>
        <input
          type="tel"
          placeholder="+44 7123 456789"
          value={phoneInput}
          onChange={(event) => handlePhoneChange(event.target.value)}
          className={`w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border-2 border-solid outline-none placeholder:text-[#A3A3A3] text-body-md transition-all ${
            errors.phone
              ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
              : 'border-shell-border focus:border-accent-blue focus:ring-4 focus:ring-aurora-primary/20'
          }`}
        />
        {phoneInput && !isValidPhoneNumber(phoneInput) && (
          <p className="text-body-sm text-red-600 mt-2">
            Enter a valid mobile number so we can send compliance updates.
          </p>
        )}
        {phoneInput && isValidPhoneNumber(phoneInput) && (
          <p className="text-body-sm text-green-600 mt-2">
            ✓ Number verified — thank you!
          </p>
        )}
      </div>

      <div>
        <label className="block text-body-md font-medium text-text-primary mb-2">
          Home address *
        </label>
        <div className="space-y-3">
          <input
            type="text"
            placeholder="House name / number and street"
            value={address.line1}
            onChange={(event) => handleAddressChange('line1', event.target.value)}
            className={`w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border-2 border-solid outline-none placeholder:text-[#A3A3A3] text-body-md transition-all ${
              errors.address
                ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                : 'border-shell-border focus:border-accent-blue focus:ring-4 focus:ring-aurora-primary/20'
            }`}
          />
          <input
            type="text"
            placeholder="Address line 2 (optional)"
            value={address.line2}
            onChange={(event) => handleAddressChange('line2', event.target.value)}
            className="w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border-2 border-solid border-shell-border outline-none focus:border-accent-blue focus:ring-4 focus:ring-aurora-primary/20 placeholder:text-[#A3A3A3] text-body-md transition-all"
          />
          <input
            type="text"
            placeholder="City"
            value={address.city}
            onChange={(event) => handleAddressChange('city', event.target.value)}
            className={`w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border-2 border-solid outline-none placeholder:text-[#A3A3A3] text-body-md transition-all ${
              errors.address
                ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                : 'border-shell-border focus:border-accent-blue focus:ring-4 focus:ring-aurora-primary/20'
            }`}
          />
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Postcode"
              value={address.postcode}
              onChange={(event) => handleAddressChange('postcode', event.target.value)}
              className={`flex-1 w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border-2 border-solid outline-none placeholder:text-[#A3A3A3] text-body-md transition-all ${
                errors.address
                  ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                  : 'border-shell-border focus:border-accent-blue focus:ring-4 focus:ring-aurora-primary/20'
              }`}
            />
            <input
              type="text"
              placeholder="Country"
              value={address.country}
              onChange={(event) => handleAddressChange('country', event.target.value)}
              className="flex-1 w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border-2 border-solid border-shell-border outline-none focus:border-accent-blue focus:ring-4 focus:ring-aurora-primary/20 placeholder:text-[#A3A3A3] text-body-md transition-all"
            />
          </div>
        </div>
        {errors.address && (
          <p className="mt-2 text-body-sm text-red-600">{errors.address}</p>
        )}
        <p className="mt-2 text-body-sm text-text-primary-secondary">
          We use this to ship hygiene starter packs and confirm you’re ready for home catering.
        </p>
      </div>
    </div>
  );
};

export default StepPersonalBasics;
